import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SideMenu from '../components/SideMenu';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { useOrders } from '../context/OrdersContext';
import { useProducts } from '../context/ProductsContext';
import OrderDetails from './OrderDetails';
import DeliveryWidgetEmbed from '../components/DeliveryWidgetEmbed';
import './webhooks.css';

function formatDateInput(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: formatDateInput(from), to: formatDateInput(to) };
}

function dateInputToIsoStart(dateValue) {
  if (!dateValue) return null;
  const d = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function dateInputToIsoEnd(dateValue) {
  if (!dateValue) return null;
  const d = new Date(`${dateValue}T23:59:59.999`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function parsePayloadFloat(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

function buildCalculationPayloadData(form) {
  return {
    reward_points: parsePayloadFloat(form.rewardPoints),
    declared_value_total: parsePayloadFloat(form.declaredValueTotal),
  };
}

function resolveActiveCompanyId(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  if (roles.includes('administrator')) {
    return (
      user?.activeCompany?.id ??
      user?.activeCompany?.company_id ??
      user?.activeCompany?.companyId ??
      ''
    );
  }
  return (
    user?.myCompany?.id ??
    user?.myCompany?.company_id ??
    user?.myCompany?.companyId ??
    user?.company?.id ??
    ''
  );
}

function resolveCompanyCurrency(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const company = roles.includes('administrator')
    ? (user?.activeCompany || user?.company || user?.myCompany)
    : (user?.myCompany || user?.company || user?.activeCompany);

  const code =
    company?.default_currency ??
    company?.currency ??
    company?.settings?.default_currency ??
    company?.settings?.currency ??
    null;

  return code ? String(code).trim().toUpperCase() : '';
}

export default function Orders() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTheme, theme } = useTheme();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { environment } = useEnvironment();
  const { orders, refreshOrders, createCalculationPayload, deleteCalculationPayload, loading, error } =
    useOrders();
  const {
    products,
    refreshProducts,
    loading: productsLoading,
    error: productsError,
  } = useProducts();

  const initialRange = useMemo(() => defaultDateRange(), []);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [search, setSearch] = useState('');
  const [savedViews, setSavedViews] = useState(() => {
    try { return JSON.parse(localStorage.getItem('filuetship.savedOrderViews') || '[]'); } catch { return []; }
  });
  const [pageSize, setPageSize] = useState(10);
  const [density, setDensity] = useState('comfortable');
  const [sort, setSort] = useState({ key: 'created_at', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [createShipmentAction, setCreateShipmentAction] = useState({
    canCreate: false,
    submitting: false,
    onCreate: null,
  });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createWizardStep, setCreateWizardStep] = useState(1);
  const [createdCalculationPayload, setCreatedCalculationPayload] = useState(null);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createForm, setCreateForm] = useState({
    orderNumber: '',
    customerId: '',
    rewardPoints: '',
    declaredValueTotal: '',
  });
  const [orderItems, setOrderItems] = useState([]);
  const [draftItem, setDraftItem] = useState({ sku: '', quantity: '1' });

  useEffect(() => {
    const requested = String(location.state?.orderNumber || '').trim();
    if (requested) setSearch(requested);
  }, [location.state]);

  useEffect(() => {
    if (!location.state?.quickCreate) return;
    openCreateModal();
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const saveCurrentView = () => {
    const name = window.prompt('Saved view name');
    if (!name?.trim()) return;
    const next = [...savedViews.filter((view) => view.name !== name.trim()), { name: name.trim(), from, to, search, pageSize }].slice(-8);
    setSavedViews(next);
    localStorage.setItem('filuetship.savedOrderViews', JSON.stringify(next));
  };

  const applySavedView = (view) => {
    setFrom(view.from); setTo(view.to); setSearch(view.search || ''); setPageSize(view.pageSize || 10);
  };

  const addedSkus = useMemo(
    () => new Set(orderItems.map((item) => item.sku)),
    [orderItems]
  );

  const availableProducts = useMemo(() => {
    return (Array.isArray(products) ? products : [])
      .filter((product) => product?.sku && !addedSkus.has(product.sku))
      .sort((a, b) => String(a.sku).localeCompare(String(b.sku), undefined, { numeric: true }));
  }, [products, addedSkus]);

  const productSkuSet = useMemo(
    () => new Set((Array.isArray(products) ? products : []).map((product) => product.sku)),
    [products]
  );

  const activeCompanyId = useMemo(
    () => String(resolveActiveCompanyId(user) || '').trim(),
    [user]
  );
  const companyCurrency = useMemo(() => resolveCompanyCurrency(user), [user]);

  useEffect(() => {
    const fromIso = dateInputToIsoStart(from);
    const toIso = dateInputToIsoEnd(to);
    if (fromIso && toIso) {
      refreshOrders({ fromDate: fromIso, toDate: toIso });
    }
  }, [from, to, refreshOrders]);

  const filtered = useMemo(() => {
    const list = Array.isArray(orders) ? orders : [];
    const q = search.trim().toLowerCase();
    const matched = !q ? list : list.filter((order) => {
      const orderNumber = String(order?.order_number || '').toLowerCase();
      const customerId = String(order?.customer_id || '').toLowerCase();
      return orderNumber.includes(q) || customerId.includes(q);
    });
    return [...matched].sort((a, b) => {
      const av = a?.[sort.key] ?? '';
      const bv = b?.[sort.key] ?? '';
      const result = sort.key === 'created_at' ? new Date(av) - new Date(bv) : String(av).localeCompare(String(bv));
      return sort.direction === 'asc' ? result : -result;
    });
  }, [orders, search, sort]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [from, to, search, pageSize]);

  const formatTs = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);

    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    const isCurrentYear = d.getFullYear() === now.getFullYear();

    try {
      if (isToday) {
        return new Intl.DateTimeFormat(undefined, {
          timeStyle: 'short',
        }).format(d);
      }
      if (isCurrentYear) {
        return new Intl.DateTimeFormat(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }).format(d);
      }
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(d);
    } catch {
      return d.toLocaleString();
    }
  };

  const formatBool = (value) => {
    if (value === true) return t('yes');
    if (value === false) return t('no');
    return '—';
  };

  const formatDeliveryCost = (order) => {
    const rawAmount = order?.delivery_cost ?? order?.raw?.selected_rate?.customer_amount;
    if (rawAmount == null || rawAmount === '') return '—';
    const numeric = Number(rawAmount);
    if (!Number.isFinite(numeric)) return String(rawAmount);
    if (!companyCurrency) {
      return numeric.toFixed(2);
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: companyCurrency,
        currencyDisplay: 'symbol',
      }).format(numeric);
    } catch {
      return `${numeric.toFixed(2)} ${companyCurrency}`;
    }
  };

  const onRefresh = () => {
    const fromIso = dateInputToIsoStart(from);
    const toIso = dateInputToIsoEnd(to);
    if (fromIso && toIso) {
      refreshOrders({ fromDate: fromIso, toDate: toIso });
    }
  };

  const openOrderDetails = (order) => {
    if (order?.order_number) navigate(`/calculations/${encodeURIComponent(order.order_number)}`);
  };

  const toggleSort = (key) => setSort((current) => ({ key, direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc' }));

  const closeOrderDetails = () => {
    if (deleteSubmitting || createShipmentAction.submitting) return;
    setSelectedOrder(null);
    setDeleteError('');
    setCreateShipmentAction({ canCreate: false, submitting: false, onCreate: null });
  };

  const deleteSelectedOrder = async () => {
    if (!selectedOrder || deleteSubmitting) return;

    if (selectedOrder.confirmed_by_customer) {
      window.alert(t('ordersDeleteConfirmed'));
      return;
    }

    const payloadId = selectedOrder.raw?.id || selectedOrder.id;
    if (!payloadId) {
      setDeleteError(t('ordersDeleteFailed'));
      return;
    }

    if (!window.confirm(t('ordersDeleteConfirm'))) {
      return;
    }

    setDeleteSubmitting(true);
    setDeleteError('');
    try {
      const result = await deleteCalculationPayload(payloadId);
      if (result?.ok) {
        closeOrderDetails();
        return;
      }
      setDeleteError(t(result?.error || 'ordersDeleteFailed'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const openCreateModal = () => {
    if (!activeCompanyId) {
      window.alert(t('ordersCreateMissingCompany'));
      return;
    }
    setCreateError('');
    setCreateWizardStep(1);
    setCreatedCalculationPayload(null);
    setCreateForm({
      orderNumber: '',
      customerId: '',
      rewardPoints: '',
      declaredValueTotal: '',
    });
    setOrderItems([]);
    setDraftItem({ sku: '', quantity: '1' });
    setCreateModalOpen(true);
    refreshProducts();
  };

  const closeCreateModal = () => {
    if (createSubmitting) return;
    setCreateModalOpen(false);
    setCreateWizardStep(1);
    setCreatedCalculationPayload(null);
    setCreateError('');
  };

  const handleWidgetComplete = useCallback(
    async ({ calculationPayloadId: payloadId }) => {
      const id = String(payloadId || '').trim();
      if (!id) {
        return;
      }

      const fromIso = dateInputToIsoStart(from);
      const toIso = dateInputToIsoEnd(to);
      let list = orders;
      if (fromIso && toIso) {
        list = await refreshOrders({ fromDate: fromIso, toDate: toIso });
      }

      const fromList = (Array.isArray(list) ? list : []).find((row) => String(row?.id) === id);
      const fallback = createdCalculationPayload
        ? {
            ...createdCalculationPayload,
            id: createdCalculationPayload.id || id,
            confirmed_by_customer: true,
            raw: {
              ...(createdCalculationPayload.raw || {}),
              id: createdCalculationPayload.raw?.id || createdCalculationPayload.id || id,
              confirmed_by_customer: true,
            },
          }
        : null;

      setCreateModalOpen(false);
      setCreateWizardStep(1);
      setCreatedCalculationPayload(null);
      setCreateError('');
      setSelectedOrder(fromList || fallback);
    },
    [from, to, orders, refreshOrders, createdCalculationPayload]
  );

  const goToCreateWizardPrev = () => {
    if (createSubmitting) return;
    setCreateError('');
    setCreateWizardStep(1);
  };

  const updateCreateField = (key, value) => {
    setCreateForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateDraftItem = (key, value) => {
    setDraftItem((prev) => ({ ...prev, [key]: value }));
  };

  const addOrderItem = () => {
    setCreateError('');
    const sku = String(draftItem.sku || '').trim();
    const quantity = Number(draftItem.quantity);
    if (!sku || !productSkuSet.has(sku)) {
      setCreateError(t('ordersCreateItemSkuRequired'));
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setCreateError(t('ordersCreateItemQuantityInvalid'));
      return;
    }
    setOrderItems((prev) => [...prev, { sku, quantity }]);
    setDraftItem({ sku: '', quantity: '1' });
  };

  const removeOrderItem = (index) => {
    setOrderItems((prev) => prev.filter((_, i) => i !== index));
  };

  const submitCreatePayload = async () => {
    if (createSubmitting) return;
    setCreateError('');

    const orderNumber = String(createForm.orderNumber || '').trim();
    const customerId = String(createForm.customerId || '').trim();

    if (!orderNumber || !customerId) {
      setCreateError(t('ordersCreateRequiredFields'));
      return;
    }

    const payloadData = buildCalculationPayloadData(createForm);

    if (payloadData.reward_points == null) {
      setCreateError(t('ordersCreateRewardPointsInvalid'));
      return;
    }
    if (payloadData.declared_value_total == null) {
      setCreateError(t('ordersCreateDeclaredValueInvalid'));
      return;
    }

    const data = {
      reward_points: payloadData.reward_points,
      declared_value_total: payloadData.declared_value_total,
    };

    const items = orderItems;
    if (!items.length) {
      setCreateError(t('ordersCreateItemsRequired'));
      return;
    }

    setCreateSubmitting(true);
    try {
      const created = await createCalculationPayload({
        order_number: orderNumber,
        customer_id: customerId,
        language: 'en',
        environment,
        data,
        items: items.map((item) => ({
          sku: item.sku,
          quantity: item.quantity,
        })),
      });
      if (!created) {
        setCreateError(t('ordersCreateFailed'));
        return;
      }
      const fromIso = dateInputToIsoStart(from);
      const toIso = dateInputToIsoEnd(to);
      if (fromIso && toIso) {
        await refreshOrders({ fromDate: fromIso, toDate: toIso });
      }
      setCreatedCalculationPayload(created);
      setCreateWizardStep(2);
      setCreateError('');
    } finally {
      setCreateSubmitting(false);
    }
  };

  return (
    <div
      className="webhooks-page"
      style={{
        '--wh-background': currentTheme.background,
        '--wh-surface': currentTheme.surface,
        '--wh-border': currentTheme.border,
        '--wh-card-shadow': currentTheme.cardShadow,
        '--wh-text': currentTheme.text,
        '--wh-text-secondary': currentTheme.textSecondary,
      }}
    >
      <SideMenu currentTheme={currentTheme} theme={theme} />

      <main className="webhooks-main">
        <h1 className="webhooks-title">{t('calculations')}</h1>

        <div className="webhooks-filters">
          <label className="webhooks-filterLabel">
            <span className="webhooks-muted">{t('From')}</span>
            <input
              type="date"
              className={`webhooks-filterInput ${theme}`}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="webhooks-filterLabel">
            <span className="webhooks-muted">{t('To')}</span>
            <input
              type="date"
              className={`webhooks-filterInput ${theme}`}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <label className="webhooks-filterLabel">
            <span className="webhooks-muted">{t('ordersSearch')}</span>
            <input
              type="search"
              className={`webhooks-filterInput ${theme}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('ordersSearchPlaceholder')}
            />
          </label>
          <button
            type="button"
            className={['webhooks-primaryBtn', theme, loading ? 'disabled' : 'enabled'].join(' ')}
            onClick={onRefresh}
            disabled={loading}
            style={{ marginLeft: 'auto' }}
          >
            {t('refresh')}
          </button>
          <button type="button" className={['webhooks-primaryBtn', theme, 'enabled'].join(' ')} onClick={saveCurrentView}>Save view</button>
        </div>
        {!!savedViews.length && <div className="saved-views" aria-label="Saved views">{savedViews.map((view) => <button type="button" key={view.name} onClick={() => applySavedView(view)}>{view.name}</button>)}</div>}

        {loading && <div className="webhooks-muted webhooks-sectionGap">{t('loading')}</div>}

        {!loading && error && (
          <div className={`webhooks-error ${theme} webhooks-sectionGap`}>{t(error)}</div>
        )}

        {!loading && !error && (
          <div className="webhooks-tableWrap webhooks-sectionGap">
            <div className={['webhooks-tableTopbar', theme].join(' ')}>
              <div className="webhooks-tableTopbarTitle">{t('ordersPayloadsListTitle')}</div>
              <div className="table-density" aria-label="Table density"><button type="button" className={density === 'compact' ? 'active' : ''} onClick={() => setDensity('compact')}>Compact</button><button type="button" className={density === 'comfortable' ? 'active' : ''} onClick={() => setDensity('comfortable')}>Comfortable</button></div>
              <button
                type="button"
                className={[
                  'webhooks-primaryBtn',
                  theme,
                  activeCompanyId ? 'enabled' : 'disabled',
                ].join(' ')}
                onClick={openCreateModal}
                disabled={!activeCompanyId}
              >
                {t('ordersCreateShipmentDraft')}
              </button>
            </div>
            <table className={`webhooks-table calculations-table ${density}`}>
              <thead>
                <tr>
                  <th className="webhooks-th"><button type="button" onClick={() => toggleSort('order_number')}>{t('ordersOrderNumber')} {sort.key === 'order_number' ? (sort.direction === 'asc' ? '↑' : '↓') : ''}</button></th>
                  <th className="webhooks-th">{t('ordersCustomerId')}</th>
                  <th className="webhooks-th">{t('ordersDetailsShipmentRawStatus')}</th>
                  <th className="webhooks-th"><button type="button" onClick={() => toggleSort('created_at')}>{t('Created')} {sort.key === 'created_at' ? (sort.direction === 'asc' ? '↑' : '↓') : ''}</button></th>
                  <th className="webhooks-th">{t('ordersConfirmed')}</th>
                  <th className="webhooks-th">{t('ordersDeliveryCost')}</th>
                  <th className="webhooks-th">{t('ordersItemsCount')}</th>
                  <th className="webhooks-th webhooks-thRight">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="webhooks-tableEmpty webhooks-muted">
                      {t('ordersEmpty')}
                    </td>
                  </tr>
                ) : (
                  pageItems.map((order, idx) => (
                    <tr
                      key={order.id || order.order_number}
                      className={[
                        (() => {
                          const shipmentStatus = order?.shipment_status ?? order?.raw?.shipment_status;
                          const normalizedStatus = String(shipmentStatus || '').trim().toLowerCase();
                          if (normalizedStatus === 'delivered') return 'webhooks-trConfirmed';
                          if (normalizedStatus === 'created') return 'webhooks-trCreated';
                          return null;
                        })(),
                        theme,
                        idx === pageItems.length - 1 ? 'webhooks-trLast' : null,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <td className="webhooks-td">
                        {order.order_number ? (
                          <button
                            type="button"
                            className="order-number-link"
                            onClick={() => openOrderDetails(order)}
                            title={`Open ${order.order_number} details`}
                          >
                            {order.order_number}
                          </button>
                        ) : '—'}
                      </td>
                      <td className="webhooks-td">{order.customer_id || '—'}</td>
                      <td className="webhooks-td">
                        {(() => {
                          const value = order?.shipment_status ?? order?.raw?.shipment_status;
                          return value == null || String(value).trim() === '' ? 'DRAFT' : String(value).toUpperCase();
                        })()}
                      </td>
                      <td className="webhooks-td">{formatTs(order.created_at)}</td>
                      <td className="webhooks-td">
                        {order.confirmed_by_customer
                          ? formatTs(order.confirmed_at)
                          : formatBool(order.confirmed_by_customer)}
                      </td>
                      <td className="webhooks-td">{formatDeliveryCost(order)}</td>
                      <td className="webhooks-td">{order.items_count ?? '—'}</td>
                      <td className="webhooks-td webhooks-actionsCell">
                        <button
                          type="button"
                          onClick={() => openOrderDetails(order)}
                          aria-label={t('orderDetailsOpen')}
                          title={t('orderDetailsOpen')}
                          className={['webhooks-iconBtn', theme].join(' ')}
                        >
                          <span className="oi oi-ellipses" aria-hidden="true" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && total > 0 && (
          <div className="webhooks-paginationBar webhooks-sectionGap">
            <div className="webhooks-fieldLabel">{t('Items per page')}</div>
            <select
              className={['webhooks-select', theme].join(' ')}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value) || 20)}
              style={{
                width: 'auto',
                minWidth: 72,
                colorScheme: theme === 'dark' ? 'dark' : 'light',
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <div className="webhooks-muted">
              {t('total')}: {total}
            </div>
            <button
              type="button"
              className={['webhooks-secondaryBtn', theme, safePage <= 1 ? 'disabled' : 'enabled'].join(' ')}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
            >
              {t('Prev')}
            </button>
            <span className="webhooks-muted">
              {t('Page')} {safePage} {t('of')} {totalPages}
            </span>
            <button
              type="button"
              className={[
                'webhooks-secondaryBtn',
                theme,
                safePage >= totalPages ? 'disabled' : 'enabled',
              ].join(' ')}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
            >
              {t('Next')}
            </button>
          </div>
        )}

        {!!selectedOrder && (
          <div
            role="dialog"
            aria-modal="true"
            className={['webhooks-modalOverlay', theme].join(' ')}
            onMouseDown={(e) => {
              if (
                e.target === e.currentTarget
                && !deleteSubmitting
                && !createShipmentAction.submitting
              ) {
                closeOrderDetails();
              }
            }}
          >
            <div className="webhooks-modal webhooks-modal--orderDetails">
              <div className="webhooks-modalHeader">
                <div className="webhooks-modalTitle">
                  {t('calculationDetailsTitle', {
                    calculationId: selectedOrder.raw?.id || '—',
                    orderNumber: selectedOrder.order_number || '—',
                  })}
                </div>
                <button
                  type="button"
                  onClick={closeOrderDetails}
                  className={['webhooks-iconBtn', theme].join(' ')}
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                  disabled={deleteSubmitting || createShipmentAction.submitting}
                >
                  ×
                </button>
              </div>

              <div className="webhooks-modalBody">
                <OrderDetails
                  order={selectedOrder}
                  onCreateShipmentAction={setCreateShipmentAction}
                />
                {deleteError && (
                  <div className={`webhooks-error ${theme} webhooks-sectionGap`}>{deleteError}</div>
                )}
              </div>
              <div className="webhooks-modalFooter">
                {!selectedOrder.confirmed_by_customer && (
                  <button
                    type="button"
                    className={[
                      'webhooks-dangerBtn',
                      theme,
                      deleteSubmitting || createShipmentAction.submitting ? 'disabled' : 'enabled',
                    ].join(' ')}
                    onClick={deleteSelectedOrder}
                    disabled={deleteSubmitting || createShipmentAction.submitting}
                  >
                    {deleteSubmitting ? t('Deleting') : t('Delete')}
                  </button>
                )}
                {createShipmentAction.canCreate && (
                  <button
                    type="button"
                    className={[
                      'webhooks-dangerBtn',
                      theme,
                      createShipmentAction.submitting || deleteSubmitting ? 'disabled' : 'enabled',
                    ].join(' ')}
                    onClick={() => createShipmentAction.onCreate?.()}
                    disabled={createShipmentAction.submitting || deleteSubmitting}
                  >
                    <span className="orders-createShipmentBtnContent">
                      <span className="oi oi-warning" aria-hidden="true" />
                      {createShipmentAction.submitting ? t('loading') : t('ordersCreateShipment')}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    deleteSubmitting || createShipmentAction.submitting ? 'disabled' : 'enabled',
                  ].join(' ')}
                  onClick={closeOrderDetails}
                  disabled={deleteSubmitting || createShipmentAction.submitting}
                >
                  {t('Cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {createModalOpen && (
          <div
            role="dialog"
            aria-modal="true"
            className={['webhooks-modalOverlay', theme].join(' ')}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeCreateModal();
            }}
          >
            <div
              className={[
                'webhooks-modal',
                createWizardStep === 2 ? 'webhooks-modal--widget' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div className="webhooks-modalHeader">
                <div className="webhooks-modalTitle">{t('ordersCreateShipmentModalTitle')}</div>
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className={['webhooks-iconBtn', theme].join(' ')}
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                  disabled={createSubmitting}
                >
                  ×
                </button>
              </div>

              <div className="webhooks-modalBody">
                {createWizardStep === 1 ? (
                <div className="webhooks-formGrid">
                  <label className="webhooks-label">
                    <span className="webhooks-fieldLabel">{t('ordersOrderNumber')}</span>
                    <input
                      className={['webhooks-input', theme].join(' ')}
                      value={createForm.orderNumber}
                      onChange={(e) => updateCreateField('orderNumber', e.target.value)}
                    />
                  </label>
                  <label className="webhooks-label">
                    <span className="webhooks-fieldLabel">{t('ordersCustomerId')}</span>
                    <input
                      className={['webhooks-input', theme].join(' ')}
                      value={createForm.customerId}
                      onChange={(e) => updateCreateField('customerId', e.target.value)}
                    />
                  </label>
                  <div className="webhooks-label">
                    <span className="webhooks-fieldLabel">{t('ordersCreateData')}</span>
                    <div className="webhooks-payloadDataGrid">
                      <label className="webhooks-orderItemField">
                        <span className="webhooks-fieldLabel">{t('ordersCreateRewardPoints')}</span>
                        <input
                          className={['webhooks-input', theme].join(' ')}
                          type="number"
                          min="0"
                          step="0.01"
                          value={createForm.rewardPoints}
                          onChange={(e) => updateCreateField('rewardPoints', e.target.value)}
                          disabled={createSubmitting}
                        />
                      </label>
                      <label className="webhooks-orderItemField">
                        <span className="webhooks-fieldLabel">{t('ordersCreateDeclaredValueTotal')}</span>
                        <input
                          className={['webhooks-input', theme].join(' ')}
                          type="number"
                          min="0"
                          step="0.01"
                          value={createForm.declaredValueTotal}
                          onChange={(e) => updateCreateField('declaredValueTotal', e.target.value)}
                          disabled={createSubmitting}
                        />
                      </label>
                    </div>
                  </div>
                  <div className="webhooks-label">
                    <span className="webhooks-fieldLabel">{t('ordersCreateItems')}</span>
                    {orderItems.length > 0 && (
                      <ul className="webhooks-orderItemsList">
                        {orderItems.map((item, index) => (
                          <li key={`${item.sku}-${index}`} className="webhooks-orderItemRow">
                            <span className="webhooks-orderItemSku">{item.sku}</span>
                            <span className="webhooks-orderItemQty">
                              {t('ordersCreateItemQuantity')}: {item.quantity}
                            </span>
                            <button
                              type="button"
                              className={['webhooks-secondaryBtn', theme, 'enabled', 'webhooks-orderItemRemove'].join(
                                ' '
                              )}
                              onClick={() => removeOrderItem(index)}
                              disabled={createSubmitting}
                            >
                              {t('ordersCreateItemRemove')}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {productsError && (
                      <div className={`webhooks-error ${theme}`} style={{ marginBottom: 8 }}>
                        {t(productsError)}
                      </div>
                    )}
                    {!productsLoading && !productsError && availableProducts.length === 0 && orderItems.length === 0 && (
                      <div className="webhooks-muted" style={{ marginBottom: 8 }}>
                        {t('productsEmpty')}
                      </div>
                    )}
                    <div className="webhooks-orderItemAddRow">
                      <label className="webhooks-orderItemField">
                        <span className="webhooks-fieldLabel">{t('ordersCreateItemSku')}</span>
                        <select
                          className={['webhooks-select', theme].join(' ')}
                          value={draftItem.sku}
                          onChange={(e) => updateDraftItem('sku', e.target.value)}
                          disabled={createSubmitting || productsLoading || availableProducts.length === 0}
                        >
                          <option value="">
                            {productsLoading ? `${t('loading')}...` : t('ordersCreateItemSkuSelect')}
                          </option>
                          {availableProducts.map((product) => (
                            <option key={product.sku} value={product.sku}>
                              {product.name ? `${product.sku} — ${product.name}` : product.sku}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="webhooks-orderItemField">
                        <span className="webhooks-fieldLabel">{t('ordersCreateItemQuantity')}</span>
                        <input
                          className={['webhooks-input', theme].join(' ')}
                          type="number"
                          min="1"
                          step="1"
                          value={draftItem.quantity}
                          onChange={(e) => updateDraftItem('quantity', e.target.value)}
                          disabled={createSubmitting}
                        />
                      </label>
                      <button
                        type="button"
                        className={['webhooks-primaryBtn', theme, createSubmitting ? 'disabled' : 'enabled'].join(
                          ' '
                        )}
                        onClick={addOrderItem}
                        disabled={
                          createSubmitting ||
                          productsLoading ||
                          !draftItem.sku ||
                          availableProducts.length === 0
                        }
                      >
                        {t('ordersCreateItemAdd')}
                      </button>
                    </div>
                  </div>
                </div>
                ) : createWizardStep === 2 && createdCalculationPayload ? (
                  <DeliveryWidgetEmbed
                    key={createdCalculationPayload.id}
                    calculationPayloadId={createdCalculationPayload.id}
                    companyId={activeCompanyId}
                    customerId={createdCalculationPayload.customer_id}
                    environment={createdCalculationPayload.environment || environment}
                    locale={i18n.language}
                    onComplete={handleWidgetComplete}
                  />
                ) : null}
                {createError && (
                  <div className={`webhooks-error ${theme} webhooks-sectionGap`}>{createError}</div>
                )}
              </div>
              <div className="webhooks-modalFooter">
                {createWizardStep === 1 ? (
                  <>
                    <button
                      type="button"
                      className={['webhooks-secondaryBtn', theme, createSubmitting ? 'disabled' : 'enabled'].join(
                        ' '
                      )}
                      onClick={closeCreateModal}
                      disabled={createSubmitting}
                    >
                      {t('Cancel')}
                    </button>
                    <button
                      type="button"
                      className={['webhooks-primaryBtn', theme, createSubmitting ? 'disabled' : 'enabled'].join(
                        ' '
                      )}
                      onClick={submitCreatePayload}
                      disabled={createSubmitting}
                    >
                      {createSubmitting ? t('Saving') : t('Next')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className={['webhooks-secondaryBtn', theme, 'enabled'].join(' ')}
                      onClick={goToCreateWizardPrev}
                    >
                      {t('Prev')}
                    </button>
                    <button
                      type="button"
                      className={['webhooks-secondaryBtn', theme, 'enabled'].join(' ')}
                      onClick={closeCreateModal}
                    >
                      {t('Cancel')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
